import { Node } from "@iventis/domain-model/model/node";
import React, { FunctionComponent, useMemo, useState } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { useTreeBrowser } from "@iventis/tree-browser";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { DialogDescription, muiInputFormsCSS, StyledInputLabel, styled } from "@iventis/styles";
import { FormWizardTemplate, Stage, FormWizardTitle } from "@iventis/components";
import { TextField } from "@mui/material";
import { useInputAutoFocus } from "@iventis/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { libraryNodeTypeFontawesome } from "./library-nodes";
import { MaxNodeNameLength } from "./validation";

export const RenameNodeComponent: FunctionComponent<{ handleModalClose: () => void; nodeToUpdate: Node }> = ({ handleModalClose, nodeToUpdate }) => {
    const [node, setNode] = useState(nodeToUpdate);

    const translate = useIventisTranslate();

    const [, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext);

    const handleUpdateNode = async () => {
        treeBrowserContext.updateNode(node);
        handleModalClose();
    };

    const buttonTextTranslated = {
        confirm: translate(Content.library.update_node.title, {
            nodeName: `${translate(Content.library.node_names[nodeToUpdate.type])}`,
        }),
    };

    const nameIsTooLong = node.name.length > MaxNodeNameLength;

    const stages: Stage[] = [
        {
            primaryButtonText: buttonTextTranslated.confirm,
            primaryButtonCallback: () => handleUpdateNode(),
            isValid: node.name.length > 0 && !nameIsTooLong,
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: handleModalClose }],
        },
    ];

    const onInput = useInputAutoFocus();

    const description = translate(Content.library.update_node.description, { nodeName: translate(Content.library.node_names[node.type]) });

    const nodeIcon = useMemo(() => <FontAwesomeIcon key="node-icon" icon={libraryNodeTypeFontawesome[nodeToUpdate.type]} />, []);

    const helperText = nameIsTooLong && translate(Content.library.create_node.maxInputChars);

    return (
        <FormWizardTemplate
            title={
                <FormWizardTitle
                    title={[
                        nodeIcon,
                        translate(Content.library.update_node.title, {
                            nodeName: `${translate(Content.library.node_names[nodeToUpdate.type])}`,
                        }),
                    ]}
                />
            }
            stages={stages}
            currentStage={0}
            isSubmitting={false}
        >
            <StyledContent>
                <DialogDescription>{description}</DialogDescription>
                <StyledInputLabel>{translate(Content.library.create_node.fieldLabel, { nodeName: translate(Content.library.node_names[node.type]) })}</StyledInputLabel>
                <TextField
                    inputRef={onInput}
                    variant="outlined"
                    error={nameIsTooLong}
                    helperText={helperText}
                    value={node.name}
                    onChange={(e) => setNode({ ...node, name: e.target.value })}
                />
            </StyledContent>
        </FormWizardTemplate>
    );
};

const StyledContent = styled.div`
    ${muiInputFormsCSS};

    .MuiFormControl-root {
        width: 100%;
    }
`;
